import React from 'react';
import { Download, Loader, Mail, UserPlus } from 'lucide-react';
import { cn } from '../../utils/cn';

interface UsersActionToolbarProps {
  onCreateUser: () => void;
  onExport: () => void;
  onResendEmails: () => void;
  resendingEmails: boolean;
}

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export const UsersActionToolbar: React.FC<UsersActionToolbarProps> = ({
  onCreateUser,
  onExport,
  onResendEmails,
  resendingEmails,
}) => {
  return (
    <section
      aria-label="Acciones de usuario"
      className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
    >
      <div className="flex flex-col gap-5 p-4 sm:p-5 lg:flex-row lg:items-stretch lg:justify-between lg:gap-6">
        <div className="min-w-0 flex-1 border-b border-slate-100 pb-4 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Acciones
          </p>
          <h2 className="mt-1 text-base font-semibold tracking-tight text-slate-900">
            Operaciones sobre usuarios
          </h2>
          <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-slate-600">
            Altas de cuenta, exportación al formato de importación y reenvío de correos de bienvenida
            pendientes.
          </p>
        </div>

        <div className="flex shrink-0 flex-col justify-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={onCreateUser}
            className={cn(
              'group inline-flex w-full items-center gap-3 rounded-xl bg-blue-600 px-4 py-3 text-left text-white shadow-sm transition-all duration-200',
              'hover:bg-blue-700 hover:shadow-md active:scale-[0.99]',
              focusRing,
              'sm:w-auto sm:min-w-[200px]'
            )}
          >
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/20 transition-colors group-hover:bg-white/20"
              aria-hidden="true"
            >
              <UserPlus className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-tight">Crear usuario</span>
              <span className="mt-0.5 block text-xs font-normal text-blue-100">
                Propietario, manager o admin
              </span>
            </span>
          </button>

          <div
            className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:overflow-hidden sm:rounded-xl sm:border sm:border-slate-200 sm:bg-slate-50 sm:p-1"
            role="group"
            aria-label="Acciones secundarias"
          >
            <button
              type="button"
              onClick={onExport}
              className={cn(
                'inline-flex w-full flex-1 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors duration-150',
                'text-slate-700 hover:bg-white hover:text-slate-900 hover:shadow-sm',
                'sm:rounded-lg sm:hover:shadow-none',
                focusRing
              )}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                aria-hidden="true"
              >
                <Download className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-tight">
                  <span className="sm:hidden">Exportar</span>
                  <span className="hidden sm:inline">Exportar Excel</span>
                </span>
                <span className="mt-0.5 hidden text-[11px] text-slate-500 sm:block">
                  Formato importación
                </span>
              </span>
            </button>

            <div className="hidden w-px self-stretch bg-slate-200 sm:block" aria-hidden="true" />

            <button
              type="button"
              onClick={onResendEmails}
              disabled={resendingEmails}
              className={cn(
                'inline-flex w-full flex-1 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors duration-150',
                'text-slate-700 hover:bg-white hover:text-slate-900 hover:shadow-sm',
                'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent disabled:hover:shadow-none',
                'sm:rounded-lg sm:hover:shadow-none',
                focusRing
              )}
            >
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-md ring-1',
                  resendingEmails
                    ? 'bg-blue-50 text-blue-600 ring-blue-100'
                    : 'bg-blue-50 text-blue-600 ring-blue-100'
                )}
                aria-hidden="true"
              >
                {resendingEmails ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-tight">
                  {resendingEmails ? (
                    'Enviando...'
                  ) : (
                    <>
                      <span className="sm:hidden">Reenviar</span>
                      <span className="hidden sm:inline">Reenviar emails</span>
                    </>
                  )}
                </span>
                <span className="mt-0.5 hidden text-[11px] text-slate-500 sm:block">
                  {resendingEmails ? 'Procesando cola' : 'Bienvenida pendiente'}
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
